import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { vcollectFormsApi } from '../../api/vcollect';
import { ApiError } from '../../api/client';
import { SectionHeader } from '../../components/SectionHeader';
import { RecordHeader } from '../../components/RecordHeader';
import { StatusBadge } from '../../components/StatusBadge';
import { DataTable } from '../../components/DataTable';
import { useToast } from '../../components/Toast';
import { VERSION_STATUS_LABELS } from './VCollectFormsListPage';

const EDITABLE_STATUSES = ['draft', 'returned_for_redraft'];

export function VCollectFormWorkspacePage() {
  const { id } = useParams<{ id: string }>();
  const formId = Number(id);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { showSuccess, showError } = useToast();

  const { data: formData, isError } = useQuery({ queryKey: ['vcollect-form', formId], queryFn: () => vcollectFormsApi.get(formId) });
  const form = formData?.data;
  const { data: versionsData } = useQuery({ queryKey: ['vcollect-form-versions', formId], queryFn: () => vcollectFormsApi.listVersions(formId) });
  const versions = versionsData?.data ?? [];

  const newVersionMutation = useMutation({
    mutationFn: () => vcollectFormsApi.createNewVersion(formId),
    onSuccess: (result) => {
      showSuccess(`Version ${result.data.versionNumber} created, copying the prior version's structure.`);
      queryClient.invalidateQueries({ queryKey: ['vcollect-form-versions', formId] });
      navigate(`/vcollect/forms/${formId}/versions/${result.data.versionId}/build`);
    },
    onError: (err: ApiError) => showError(err.message),
  });

  if (isError) return <div className="page"><div className="error-state">Failed to load this form. Please try refreshing the page.</div></div>;
  if (!form) return <div className="page"><div className="loading-state"><span className="loading-spinner" />Loading…</div></div>;

  const latestVersion = versions[0];
  const canCreateNewVersion = latestVersion && !EDITABLE_STATUSES.includes(latestVersion.status);

  return (
    <div className="page">
      <RecordHeader
        title={form.title}
        readOnly={true}
        entityType="vcollect_form"
        entityId={formId}
        fields={[
          { label: 'Form ID', value: form.form_id },
          { label: 'Project', value: form.vcollect_project_name },
          { label: 'Default Language', value: form.default_language },
        ]}
      />

      <SectionHeader title="Versions" actions={
        canCreateNewVersion ? <button onClick={() => newVersionMutation.mutate()} disabled={newVersionMutation.isPending}>Create New Version</button> : undefined
      } />

      <DataTable
        rows={versions}
        rowKey={(v) => v.id}
        columns={[
          { header: 'Version', render: (v) => `v${v.version_number}` },
          { header: 'Status', render: (v) => <StatusBadge status={v.status} label={VERSION_STATUS_LABELS[v.status]} /> },
          {
            header: '', render: (v) => (
              <a onClick={() => navigate(`/vcollect/forms/${formId}/versions/${v.id}/build`)} className="table-link">
                {EDITABLE_STATUSES.includes(v.status) ? 'Build →' : 'View →'}
              </a>
            ),
          },
        ]}
      />
    </div>
  );
}
